import { useEffect, useMemo, useRef, useState } from "react";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";

type DownloadPayload = {
  isCustomGridDownload: boolean;
  from: number;
  to: number;
};

type WorkspaceDownloadControlProps = {
  selectedCount?: number;
  totalRows?: number;
  onDownload?: (payload: DownloadPayload) => Promise<void>;
  loading?: boolean;
  creditsPerDownload?: number;
};

const WorkspaceDownloadControl = ({ selectedCount = 0, totalRows = 0, onDownload, loading = false, creditsPerDownload = 5 }: WorkspaceDownloadControlProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [downloadFromValue, setDownloadFromValue] = useState<number | string>(1);
  const [downloadToValue, setDownloadToValue] = useState<number | string>(100);
  const [isCustomGridDownload, setIsCustomGridDownload] = useState(false);
  const [confirmFromRange, setConfirmFromRange] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };
    if (showOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptions]);

  const rangeCount = useMemo(() => {
    const from = +downloadFromValue;
    const to = +downloadToValue;
    if (!from || !to || to < from) return 0;
    return to - from + 1;
  }, [downloadFromValue, downloadToValue]);

  const isRangeValid = useMemo(() => {
    const from = +downloadFromValue;
    const to = +downloadToValue;
    return Number.isInteger(from) && Number.isInteger(to) && from >= 1 && to >= from && to <= totalRows;
  }, [downloadFromValue, downloadToValue, totalRows]);

  const downloadCount = selectedCount > 0 ? selectedCount : rangeCount;
  const totalCredits = downloadCount * creditsPerDownload;

  const openDownloadFlow = (customGrid: boolean) => {
    setShowOptions(false);
    setIsCustomGridDownload(customGrid);
    if (selectedCount > 0) {
      setShowConfirmModal(true);
      return;
    }
    const maxRange = totalRows < 100 ? totalRows : 100;
    setDownloadFromValue(1);
    setDownloadToValue(maxRange > 0 ? maxRange : 1);
    setShowRangeModal(true);
  };

  const onConfirmDownload = async () => {
    const payload: DownloadPayload = {
      isCustomGridDownload,
      from: +downloadFromValue,
      to: +downloadToValue,
    };
    trademoTrackingEvents(trackingEventDictionary.workspaceDownloadConfirm, { download_type: isCustomGridDownload ? "columns_in_grid" : "all_columns" });
    await onDownload?.(payload);
    setShowConfirmModal(false);
    setShowRangeModal(false);
    setConfirmFromRange(false);
  };

  return (
    <>
      <div className="tw-relative" ref={optionsRef}>
        <button className="tw-h-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5 hover:tw-bg-gray-50 tw-shadow-button" onClick={() => setShowOptions((prev) => !prev)}>
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
            <path d="M480-336 288-528l51-51 105 105v-342h72v342l105-105 51 51-192 192ZM263.72-192Q234-192 213-213.15T192-264v-72h72v72h432v-72h72v72q0 29.7-21.16 50.85Q725.68-192 695.96-192H263.72Z" />
          </svg>
          Download
        </button>
        {showOptions ? (
          <div className="tw-absolute tw-top-[calc(100%+8px)] tw-left-0 tw-z-30 tw-w-[190px] tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-shadow-button tw-px-4 tw-pt-3">
            <button
              className="tw-w-full tw-text-left tw-border-0 tw-bg-transparent tw-p-0 tw-mb-3 tw-text-xs tw-font-semibold tw-text-gray-900"
              onClick={() => {
                trademoTrackingEvents(trackingEventDictionary.workspaceDownloadAllColumns, {});
                openDownloadFlow(false);
              }}>
              All Columns
            </button>
            <button
              className="tw-w-full tw-text-left tw-border-0 tw-bg-transparent tw-p-0 tw-mb-3 tw-text-xs tw-font-semibold tw-text-gray-900"
              onClick={() => {
                trademoTrackingEvents(trackingEventDictionary.workspaceDownloadColumnsInGrid, {});
                openDownloadFlow(true);
              }}>
              Columns Shown in Grid
            </button>
          </div>
        ) : null}
      </div>

      {showRangeModal ? (
        <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-items-center tw-justify-center tw-bg-black/40">
          <div className="tw-w-full tw-max-w-[560px] tw-rounded-xl tw-bg-white tw-shadow-xl">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-5 tw-py-3">
              <h3 className="tw-text-base tw-font-semibold tw-text-gray-900">Download Workspace Companies</h3>
              <button className="tw-border-0 tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700" onClick={() => setShowRangeModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="tw-px-5 tw-py-4">
              <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-items-center">
                <div className="tw-col-span-6 tw-text-sm tw-text-gray-700">Select companies by S.No</div>
                <div className="tw-col-span-6 tw-grid tw-grid-cols-12 tw-gap-2 tw-items-center">
                  <input
                    className="tw-col-span-5 tw-h-[35px] tw-w-full tw-rounded-md tw-border tw-border-[#d9d9d9] tw-pl-2.5 tw-text-base"
                    placeholder="From"
                    type="text"
                    value={downloadFromValue}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) setDownloadFromValue(e.target.value);
                    }}
                  />
                  <span className="tw-col-span-2 tw-text-center tw-text-sm tw-text-gray-500">to</span>
                  <input
                    className="tw-col-span-5 tw-h-[35px] tw-w-full tw-rounded-md tw-border tw-border-[#d9d9d9] tw-pl-2.5 tw-text-base"
                    placeholder="To"
                    type="text"
                    value={downloadToValue}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) setDownloadToValue(e.target.value);
                    }}
                  />
                </div>
              </div>
              {!isRangeValid && (downloadFromValue !== "" || downloadToValue !== "") && <p className="tw-mt-2 tw-text-xs tw-text-red-500">Enter values between 1 and {totalRows}</p>}
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-gray-200 tw-px-5 tw-py-3">
              <button className="tw-text-sm tw-font-medium tw-text-gray-900 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-w-fit" onClick={() => setShowRangeModal(false)}>
                Cancel
              </button>
              <button
                className="tw-text-sm tw-font-medium tw-text-white tw-bg-primary-purple-500 tw-rounded-md tw-px-3 tw-py-2 tw-w-fit disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                disabled={!isRangeValid}
                onClick={() => {
                  setShowRangeModal(false);
                  setConfirmFromRange(true);
                  setShowConfirmModal(true);
                }}>
                Download
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showConfirmModal ? (
        <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-flex tw-items-center tw-justify-center tw-bg-black/40">
          <div className="tw-w-full tw-max-w-[560px] tw-rounded-xl tw-bg-white tw-shadow-xl">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-5 tw-py-3">
              <h3 className="tw-text-base tw-font-semibold tw-text-gray-900">Confirmation</h3>
              <button className="tw-border-0 tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700" onClick={() => setShowConfirmModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="tw-px-5 tw-py-4 tw-text-sm tw-text-gray-700">
              You are downloading list of <strong>{downloadCount} Companies</strong> which will cost you <strong>{totalCredits} Download Credits.</strong>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-gray-200 tw-px-5 tw-py-3">
              <button
                className="tw-text-sm tw-font-medium tw-text-gray-900 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-px-3 tw-py-2 tw-w-fit"
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmFromRange) {
                    setConfirmFromRange(false);
                    setShowRangeModal(true);
                  }
                }}>
                {confirmFromRange ? "Back" : "Cancel"}
              </button>
              <button className="tw-text-sm tw-font-medium tw-text-white tw-bg-primary-purple-500 tw-rounded-md tw-px-3 tw-py-2 tw-w-fit" onClick={onConfirmDownload} disabled={loading}>
                {loading ? "Downloading..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default WorkspaceDownloadControl;
